import React from "react";
import { cn } from "../../../../../shared/ui/cn";
import { LAYOUT } from "../../../../../shared/layout";

export type SessionDockButtonProps = {
  icon: React.ReactNode;
  /** The control's name. Shown on small screens, and always its accessible name. */
  label: string;
  active: boolean;
  onClick: () => void;
  /** The dock is a rail beside the form on desktop and a bar below it on small screens. */
  compact: boolean;
};

/**
 * One button in the session dock.
 *
 * The dock had five of these written out longhand, each with a three-way nested
 * ternary and its own colour — blue, brand, blue, blue, purple — so the same
 * control meant a different thing depending on which panel it opened. They
 * share one treatment now.
 *
 * Each one toggles a panel, so it carries `aria-pressed`. Previously they had
 * only a `title`, which is a tooltip rather than a name and is invisible to
 * touch users on the very screens where the label is hidden.
 */
const SessionDockButton: React.FC<SessionDockButtonProps> = ({
  icon,
  label,
  active,
  onClick,
  compact,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    aria-pressed={active}
    style={compact ? { minHeight: LAYOUT.TOUCH_TARGET_SIZE } : undefined}
    className={cn(
      "flex items-center justify-center transition-colors duration-[--duration-fast] ease-[--ease-standard]",
      compact
        ? "min-w-11 flex-col gap-1 rounded-lg px-2 py-1"
        : "size-11 rounded-lg border",
      active
        ? compact
          ? "bg-accent-tint text-accent-on-tint"
          : "border-transparent bg-accent text-accent-contrast"
        : compact
          ? "text-content-secondary hover:bg-surface-sunken hover:text-content-primary"
          : "border-border-subtle bg-surface-raised text-content-secondary hover:border-border-strong hover:text-content-primary"
    )}
  >
    <span aria-hidden="true">{icon}</span>
    {compact && (
      <span className="max-w-16 truncate text-xs font-medium">{label}</span>
    )}
  </button>
);

export default SessionDockButton;
